import {
  type ActiveFiltersState,
  type AdvancedFilterState,
} from '../../../shared/types';

export enum DetailsType {
  SLICE_NAME = 'aag/details',
  API_SLICE_NAME = 'aag/detailsApi',
}

export interface ViolationConflictTrackers {
  id: number;
  level: number;
  childrenCount: number;
  type: string;
}

export interface ViolationFunctions {
  id: number;
  name: string;
  description: string | null;
  accountName: string;
  endpointName: string;
  assignedEntitlementsCount: number | null;
  endpointLogo: string | null;
}

export interface TrackedViolationFunctions extends ViolationFunctions {
  tracker: ViolationConflictTrackers;
}
export interface ViolationAssignedEntitlements {
  violationKey: number;
  functionKey: number;
  entitlementKey: number;
  entitlementValue: string | null;
  entitlementTypeName: string | null;
  entitlementDisplayName: string;
  entitlementTrustScore: number;
  occurrencesInFunctions: number;
  soxSeverity: string;
  sysSeverity: string;
  privilegedSeverity: string;
  startDate: string | null;
  endDate: string | null;
  totalViolatingEntitlements: number | null;
  description: string | null;
  glossary: string | null;
  customProperty1: string | null;
}
export interface TrackedViolationAssignedEntitlements
  extends ViolationAssignedEntitlements {
  tracker: ViolationConflictTrackers;
}

export interface ViolatingEntitlementObjects {
  fieldName: string;
  userValue: string;
  ruleValue: string;
  showObjectFieldLabel: boolean;
  objectValue: string;
}

export interface ViolatingEntitlements {
  violationKey: number;
  functionKey: number;
  entitlementKey: number;
  entitlementValue: string | null;
  entitlementTypeName: string | null;
  entitlementDisplayName: string;
  objectValue: string;
  entitlementTrustScore: number;
  fieldName: string | null;
  fieldValue: string;
  userValue: string | null;
  ruleValue: string | null;
  firstUsed: string;
  lastUsed: string;
  totalUsed: number | null;
  object?: ViolatingEntitlementObjects[] | null;
  showObjectFieldLabel: boolean;
  soxSeverity: string;
  sysSeverity: string;
  privilegedSeverity: string;
  startDate: string | null;
  endDate: string | null;
  description: string | null;
  glossary: string | null;
  customProperty1: string | null;
}

export interface TrackedViolatingEntitlements extends ViolatingEntitlements {
  violationId: string;
  assignedEntitlementId: string;
  tracker: ViolationConflictTrackers;
}

export interface ViolationMitigatingControls {
  id: number;
  name: string;
  approvedBy: string;
  approverName: string;
  preMitigated: boolean;
  description: string;
  controlType: string;
  comments: string;
  startDate: string;
  endDate: string;
  expiryDate: string;
  expiryStatus: 0 | 1 | 2; // 0 (Active), 1 (Expires soon), 2 (Expired)
  priority: string | number;
}
interface ViolationsTabDetailsConflictControls {
  filters: AdvancedFilterState[];
  activeFilters: ActiveFiltersState[];
  searchKey: string | null;
  expandKey: string | null;
}

export interface DetailsState {
  source: string | null;
  queryParam: string | undefined;
  sidepanel: { isSidePanelVisible: boolean };
  violationsTab: {
    isFooterVisible: boolean;
    conflicts: {
      selectedRowData: TrackedViolationFunctions | null;
      controls: ViolationsTabDetailsConflictControls;
    };
    mitigate: {
      selectedRowData: ViolationMitigatingControls | null;
    };
  };
}

// API queries
export interface ViolationConflictApisProp {
  violationId: string;
  functionId?: string;
  assignedEntitlementId?: string;
  payload: {
    violatingSoxCriticality: string[];
    violatingSysCriticality: string[];
    violatingPrivileged: string[];
    violatingEntitlements: string[];
    assignedEntitlements: string[];
    globalSearch: string;
    afterKey?: string | null;
    searchAfter?: string[] | null;
    max?: number;
  };
}

// API responses
interface GetApiResponse {
  status?: number;
  errorCode?: string;
  displayRecords?: number;
  displayCount?: number;
  displaycount?: number;
}

export interface GetViolationFunctionApiResponse extends GetApiResponse {
  data: ViolationFunctions[];
}

export interface GetAssignedEntitlementApiResponse extends GetApiResponse {
  data: ViolationAssignedEntitlements[];
  afterKey: string | null;
}

export interface GetViolationMitigatingControlApiResponse
  extends GetApiResponse {
  data: ViolationMitigatingControls[];
}

export interface GetViolatingEntitlementApiResponse extends GetApiResponse {
  data: ViolatingEntitlements;
  searchAfter: string[] | null;
}
